/**
 * Calls `method` of `object` which is an asynchronous function taking an
 * error-first Node.js-style callback, and returns a promise to its result.
 *
 * @param object - An object.
 * @param method - Name of a method of the `object`. This method must be an
 * asynchronous function which takes a callback.
 * @param args - Arguments of `method`, except the last one (i.e., callback).
 *
 * @returns Promise that:
 *  - Resolves to a single value if `method` invokes its callback with the first
 *    argument (error) being falsy and the second argument being the only one.
 *  - Resolves to an array of values if `method` invokes its callback with the
 *    first argument (error) being falsy and more than two arguments in general.
 *    The array contains all the arguments except the first one.
 *  - Rejects with the first argument of callback if it's a truthy value.
 */
const spawn = <O>(object: O, method: keyof O, ...args: any[]): Promise<any> =>
  new Promise((resolve, reject) => {
    (object[method] as any)(...args, (error: Error, ...result: any[]) => {
      if (error) {
        reject(error);
      } else if (result.length <= 1) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        resolve(...result);
      } else {
        resolve(result);
      }
    });
  });

export default spawn;
